import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Notification, NotificationCategory } from '@proref/types';
import { useMarkNotificationsRead, useNotificationsListQuery } from '../hooks/use-notifications';
import { timeAgo } from '../lib/format';
import { PageHeader } from '../components/shared/PageHeader';
import { ListSkeleton } from '../components/shared/Skeleton';
import { Button } from '../components/shared/Button';
import { Icon } from '../components/shared/Icons';

// Same coarse mapping the bell uses — see NotificationBell for why it is per-category rather than
// per-type.
const CATEGORY_ICON: Record<NotificationCategory, keyof typeof Icon> = {
  daily: 'spark',
  exams: 'flask',
  webinars: 'calendar',
  discussion: 'send',
  admin: 'building',
  platform: 'chart',
  account: 'lock',
};

// An absolute timestamp alongside the relative one: on the full page, unlike in the dropdown, "when
// exactly" is a reasonable thing to want, and a list of nothing but "3d ago" is hard to scan.
function absoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface RowProps {
  notification: Notification;
  onOpen: (n: Notification) => void;
}

function NotificationListRow({ notification, onOpen }: RowProps) {
  const Glyph = Icon[CATEGORY_ICON[notification.category] ?? 'bell'];
  const unread = notification.read_at === null;

  const inner = (
    <>
      <span
        className={
          'mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full ' +
          (unread ? 'bg-accent-tint text-accent' : 'bg-surface-2 text-muted')
        }
      >
        <Glyph className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span className="min-w-0 flex-1 text-[14px] font-medium leading-snug text-ink">
            {notification.title}
          </span>
          {unread && (
            <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-accent" aria-label="Unread" />
          )}
        </span>
        {notification.body && (
          <span className="mt-1 block text-[13px] leading-relaxed text-muted">{notification.body}</span>
        )}
        <span className="mt-1.5 block text-[11px] text-muted-2">
          {timeAgo(notification.created_at)} · {absoluteTime(notification.created_at)}
        </span>
      </span>
    </>
  );

  const className =
    'flex gap-3 border-b border-line px-4 py-3.5 last:border-b-0 transition-colors ' +
    (unread ? 'bg-accent-tint/25 ' : '') +
    (notification.link ? 'hover:bg-surface-2' : '');

  // Unlinked entries stay plain text — see NotificationBell for the reasoning.
  if (!notification.link) return <div className={className}>{inner}</div>;

  return (
    <Link to={notification.link} className={className} onClick={() => onOpen(notification)}>
      {inner}
    </Link>
  );
}

// The full notification history, newest first, with an All / Unread filter and mark-all-read.
//
// The list is keyset-paged rather than offset-paged: new notifications arrive at the TOP while the
// user is reading, and an offset page 2 would re-show rows that page 1 had already pushed down.
export function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotificationsListQuery(unreadOnly);
  const markRead = useMarkNotificationsRead();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);
  const unreadCount = data?.pages[0]?.meta.unread_count ?? 0;

  // Auto-load the next page as the sentinel scrolls into view. Guarded because jsdom (and any
  // browser without it) has no IntersectionObserver — there the "Load more" button below is the
  // whole mechanism, which is also why that button is always rendered rather than being a fallback.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver !== 'function') return;
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void fetchNextPage();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div>
      <PageHeader
        title="Notifications"
        sub="Everything ProRef has told you, newest first."
        actions={
          unreadCount > 0 ? (
            <Button size="sm" onClick={() => markRead.mutate({ all: true })}>
              Mark all as read
            </Button>
          ) : undefined
        }
      />

      {/* All / Unread. Local state, not a route: unlike the app's tab strips this is a transient
          filter on one list, not a destination anybody links a colleague to. */}
      <div className="mb-4 flex items-center gap-1">
        {([false, true] as const).map((value) => (
          <button
            key={String(value)}
            type="button"
            onClick={() => setUnreadOnly(value)}
            className={
              'rounded-full px-3 py-1 text-[12px] font-medium transition-colors ' +
              (unreadOnly === value
                ? 'bg-ink text-bg'
                : 'bg-surface-2 text-muted hover:text-ink')
            }
          >
            {value ? `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` : 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <ListSkeleton rows={6} />
      ) : isError ? (
        // Fetch failures render in place of the data rather than as a toast (CLAUDE.md §4).
        <div className="rounded border border-line bg-surface p-8 text-center">
          <p className="text-[13px] text-muted">Could not load your notifications.</p>
          <Button size="sm" className="mt-3" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded border border-line bg-surface p-10 text-center">
          <Icon.bell className="mx-auto h-5 w-5 text-muted-2" />
          <p className="mt-3 text-[13px] text-muted">
            {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded border border-line bg-surface">
            {items.map((n) => (
              <NotificationListRow
                key={n.id}
                notification={n}
                // Opening a linked entry marks just that one read — the rest of the page is
                // untouched, so a list skimmed but not read keeps its markers.
                onOpen={(item) => {
                  if (item.read_at === null) markRead.mutate({ ids: [item.id] });
                }}
              />
            ))}
          </div>

          <div ref={sentinelRef} className="mt-4 text-center">
            {hasNextPage ? (
              <Button size="sm" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            ) : (
              <p className="text-[12px] text-muted-2">{`That’s everything.`}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
